"use client";

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/providers/AuthProvider";
import { useCreatePageStore } from "@/stores/createPageStore";
import { REFERENCE_IMAGE_FORMAT_LABEL } from "@/lib/create/referenceImageUpload";
import {
  describeReferenceImageSelection,
  prepareReferenceImages,
  uploadPreparedReferenceImages,
  type PreparedReferenceImage,
} from "@/lib/create/referenceImageUploadClient";
import { ensureAnonymousSession } from "@/lib/auth/anonymousSession";
import { useToast } from "@/context/ToastContext";
import { trackButtonClick } from "@/utils/analytics";
import {
  ENTRY_SUGGESTED_PROMPTS,
  type CreateEntryPointProps,
  type EntryConversationOptions,
} from "./entryOptions";
export function useCreateEntry({
  onStartConversation,
  entryActionPending = false,
  initialPrompt = "",
  initialReferences = [],
  initialPersistAsDesignPrompt = true,
  initialEntryPoint,
  initialPresetType,
}: CreateEntryPointProps) {
  const loading = useCreatePageStore(
    store => store.generation.progress.loading
  );
  const entryTransition = useCreatePageStore(
    store => store.agent.entryTransition
  );
  const { showToast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const [entryInputValue, setEntryInputValue] = useState(initialPrompt);
  const [selectedSuggestion, setSelectedSuggestion] = useState<
    (typeof ENTRY_SUGGESTED_PROMPTS)[number] | null
  >(null);
  const [uploadPreparing, setUploadPreparing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedReferences, setSelectedReferences] =
    useState<PreparedReferenceImage[]>(initialReferences);
  const [attachmentStatus, setAttachmentStatus] = useState("");
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const submitPendingRef = useRef(false);

  useEffect(() => {
    // Restoration can leave the shared transition at `starting` even when the
    // layout has no transcript, image, or composer text to render. In that
    // case recover the prompt controls immediately. A real first turn is owned
    // by CreateAgentLayout, which stays mounted across the temporary auth
    // restoration shell and advertises that ownership through this prop.
    const store = useCreatePageStore.getState();
    if (store.agent.entryTransition === "starting" && !entryActionPending) {
      store.setEntryTransition("idle");
    }
  }, [entryActionPending, entryTransition]);

  const startConversation = async (
    message: string,
    options: EntryConversationOptions = {}
  ) => {
    const trimmedMessage = message.trim();
    const references = selectedReferences;
    if (
      (!trimmedMessage && references.length === 0) ||
      loading ||
      authLoading ||
      uploadPreparing ||
      entryTransition === "starting" ||
      entryActionPending ||
      submitPendingRef.current
    ) {
      return;
    }

    submitPendingRef.current = true;
    setUploadError(null);
    try {
      if (references.length === 0) {
        onStartConversation(trimmedMessage, options);
        setEntryInputValue("");
        setSelectedSuggestion(null);
        return;
      }

      // File selection is local-only. Establish ownership and persist the
      // prepared references only when the customer explicitly sends the turn,
      // so cancelling an attachment never creates an anonymous account or a
      // public storage object.
      setUploadPreparing(true);
      const identityResult = user
        ? { ok: true, created: false }
        : await ensureAnonymousSession();
      const dataUrls = references.map(entry => entry.dataUrl);
      const hostedUrls = identityResult.ok
        ? await uploadPreparedReferenceImages(dataUrls, "front")
        : null;
      const resolvedMessage =
        trimmedMessage ||
        (references.length === 1
          ? "Use this photo or sketch as inspiration for my dress."
          : "Use these photos or sketches as inspiration for my dress.");

      onStartConversation(resolvedMessage, {
        ...options,
        entryPoint: "reference_upload",
        persistAsDesignPrompt:
          Boolean(trimmedMessage) &&
          (trimmedMessage !== initialPrompt.trim() ||
            initialPersistAsDesignPrompt),
        presetType: options.presetType ?? "Upload",
        identityReady: identityResult.ok,
        identityCreated: identityResult.created,
        referenceImages: {
          files: references.map(entry => entry.file),
          urls: hostedUrls ?? dataUrls,
          previewUrls: dataUrls,
        },
      });
      setEntryInputValue("");
      setSelectedSuggestion(null);
      setSelectedReferences([]);
      setAttachmentStatus("Inspiration photos sent.");
    } catch {
      setUploadError(
        "We couldn't prepare that image for your designer. Please try again."
      );
    } finally {
      submitPendingRef.current = false;
      setUploadPreparing(false);
    }
  };

  const handleSuggestedPrompt = (
    suggestion: (typeof ENTRY_SUGGESTED_PROMPTS)[number]
  ) => {
    // A ghost suggestion may only be accepted into an empty draft. Typing or
    // pasting always belongs wholly to the customer, never to the animation.
    if (entryInputValue || entryDisabled) return;
    trackButtonClick({
      button_id: `create_prompt_${suggestion.id}`,
      button_text: suggestion.message,
      page_section: "create_prompt_starters",
    });
    setEntryInputValue(suggestion.message);
    setSelectedSuggestion(suggestion);
  };

  const handleInputChange = (value: string) => {
    setEntryInputValue(value);
    setSelectedSuggestion(null);
  };

  const handleCustomPrompt = (message: string) => {
    const removedInitialReference =
      initialReferences.length > 0 && selectedReferences.length === 0;
    const isUnchangedRetry =
      Boolean(initialPrompt.trim()) &&
      message.trim() === initialPrompt.trim() &&
      Boolean(initialEntryPoint) &&
      !removedInitialReference;
    const isUnchangedSuggestion =
      selectedReferences.length === 0 &&
      selectedSuggestion !== null &&
      message.trim() === selectedSuggestion.message;
    // File selection already records the upload affordance; image turns are
    // durably attributed as `reference_upload` by startConversation. Avoid a
    // contradictory `custom_prompt` event for an image send or an unchanged
    // retry of a previously attributed suggestion.
    if (
      selectedReferences.length === 0 &&
      !isUnchangedRetry &&
      !isUnchangedSuggestion
    ) {
      trackButtonClick({
        button_id: "create_prompt_custom",
        button_text: "Write your own",
        page_section: "create_prompt_starters",
      });
    }
    void startConversation(
      message,
      isUnchangedSuggestion
        ? {
            entryPoint: selectedSuggestion.entryPoint,
            presetType: selectedSuggestion.presetType,
            persistAsDesignPrompt: selectedSuggestion.persistAsDesignPrompt,
          }
        : isUnchangedRetry
          ? {
              entryPoint: initialEntryPoint,
              ...(initialPresetType && { presetType: initialPresetType }),
              persistAsDesignPrompt: initialPersistAsDesignPrompt,
            }
          : { entryPoint: "custom_prompt" }
    );
  };

  const handleReferenceUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0 || uploadPreparing) return;

    setUploadError(null);
    setUploadPreparing(true);
    trackButtonClick({
      button_id: "create_prompt_upload",
      button_text: "Upload a photo or sketch",
      page_section: "create_prompt_starters",
    });

    try {
      const prepared = await prepareReferenceImages(files);
      const { entries, rejectedCount } = prepared;
      if (entries.length === 0) {
        setUploadError(
          `Choose ${REFERENCE_IMAGE_FORMAT_LABEL} up to 5 MB, or try a smaller screenshot.`
        );
        return;
      }
      const partialSelectionNotice = describeReferenceImageSelection(
        entries.length,
        rejectedCount
      );
      if (partialSelectionNotice) {
        showToast(partialSelectionNotice, "warning");
      }
      setSelectedReferences(entries);
      setAttachmentStatus(
        entries.length === 1
          ? "1 inspiration photo attached."
          : `${entries.length} inspiration photos attached.`
      );
    } catch {
      setUploadError("We couldn't read that image. Please try another file.");
    } finally {
      setUploadPreparing(false);
    }
  };

  const clearSelectedReferences = () => {
    const removedCount = selectedReferences.length;
    setSelectedReferences([]);
    if (
      initialReferences.length > 0 &&
      !initialPersistAsDesignPrompt &&
      entryInputValue.trim() === initialPrompt.trim()
    ) {
      setEntryInputValue("");
    }
    setUploadError(null);
    setAttachmentStatus(
      removedCount === 1
        ? "Inspiration photo removed."
        : "Inspiration photos removed."
    );
    window.requestAnimationFrame(() => {
      document
        .querySelector<HTMLTextAreaElement>(
          'textarea[aria-label="Chat message input"]'
        )
        ?.focus({ preventScroll: true });
    });
  };

  const entryStarting = entryTransition === "starting";
  const entryDisabled =
    loading ||
    authLoading ||
    uploadPreparing ||
    entryStarting ||
    entryActionPending;
  const selectedReferenceCount = selectedReferences.length;

  return {
    uploadInputRef,
    handleReferenceUpload,
    uploadPreparing,
    uploadError,
    entryDisabled,
    entryStarting,
    attachmentStatus,
    entryInputValue,
    handleInputChange,
    handleCustomPrompt,
    handleSuggestedPrompt,
    selectedReferences,
    selectedReferenceCount,
    clearSelectedReferences,
  };
}
